import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Database, Download, Upload, Info, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PROD_URL = "charger-service-hub-1.emergent.host";

export default function DataMigration() {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef();

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await axios.get(`${API}/admin/export-backup`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const ts = new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-");
      const a = document.createElement("a");
      a.href = url;
      a.download = `xstrong_backup_${ts}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded successfully!");
    } catch {
      toast.error("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);

    const fd = new FormData();
    fd.append("file", file);

    try {
      const { data } = await axios.post(`${API}/admin/import-backup`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      toast.success(`Import complete! ${data.inserted} inserted, ${data.updated} updated.`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Import failed. Check the file and try again.");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div
      className="min-h-screen bg-gray-50 p-6"
      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
    >
      <div className="max-w-2xl mx-auto">
        {/* ── Page Header ── */}
        <div className="flex items-center gap-4 mb-7">
          <div className="w-14 h-14 bg-lime-500 rounded-2xl flex items-center justify-center shadow-lg shadow-lime-900/25 flex-shrink-0">
            <Database className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1
              className="text-3xl font-bold text-gray-900 tracking-tight"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Data Migration
            </h1>
            <p className="text-gray-500 text-sm mt-0.5">
              Transfer all your data from preview → production ({PROD_URL})
            </p>
          </div>
        </div>

        {/* ── How-To Info Box ── */}
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 mb-5">
          <div className="flex items-center gap-2 mb-4">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <p className="font-bold text-blue-800 text-sm">
              How to transfer your data in 2 steps:
            </p>
          </div>
          <div className="space-y-3">
            {/* Step 1 */}
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-black flex-shrink-0 mt-0.5">
                1
              </span>
              <p className="text-sm text-gray-700 leading-relaxed">
                Click{" "}
                <strong className="text-blue-700">"Export Backup"</strong>{" "}
                below — downloads a{" "}
                <code className="bg-white border border-blue-200 px-1.5 py-0.5 rounded text-xs font-mono text-gray-700">
                  .json
                </code>{" "}
                file with ALL your data (tickets, settings)
              </p>
            </div>
            {/* Arrow */}
            <p className="ml-9 text-xs text-gray-400 italic">
              → Then go to your production site →
            </p>
            {/* Step 2 */}
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-black flex-shrink-0 mt-0.5">
                2
              </span>
              <p className="text-sm text-gray-700 leading-relaxed">
                Open{" "}
                <strong className="text-blue-700">{PROD_URL}</strong> → go to{" "}
                <strong>Admin Panel → Data Migration</strong> → click{" "}
                <strong className="text-blue-700">"Import Backup"</strong> →
                select the file you downloaded
              </p>
            </div>
          </div>
        </div>

        {/* ── Step 1 Export ── */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-4 shadow-sm">
          <div className="flex items-center gap-4 mb-5">
            <span className="w-10 h-10 bg-lime-500 text-white rounded-full flex items-center justify-center text-lg font-black flex-shrink-0 shadow-sm shadow-lime-900/20">
              1
            </span>
            <div>
              <h3
                className="font-bold text-gray-900 text-base"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Export Backup — from THIS environment
              </h3>
              <p className="text-gray-500 text-sm">
                Downloads all your data as a single JSON file
              </p>
            </div>
          </div>

          <Button
            onClick={handleExport}
            disabled={exporting}
            data-testid="export-backup-btn"
            className="bg-lime-600 hover:bg-lime-700 text-white font-bold px-6 shadow-sm shadow-lime-900/20 transition-all duration-200"
          >
            <Download className="w-4 h-4 mr-2" />
            {exporting ? "Exporting..." : "Export Backup (Download JSON)"}
          </Button>
        </div>

        {/* ── Step 2 Import ── */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-4 mb-5">
            <span className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center text-lg font-black flex-shrink-0 shadow-sm shadow-blue-900/20">
              2
            </span>
            <div>
              <h3
                className="font-bold text-gray-900 text-base"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Import Backup — into THIS environment
              </h3>
              <p className="text-gray-500 text-sm">
                Do this step on{" "}
                <strong className="text-blue-700">{PROD_URL}</strong> — upload
                the file from Step 1
              </p>
            </div>
          </div>

          {/* Upload button */}
          <label
            className={importing ? "cursor-not-allowed" : "cursor-pointer"}
            data-testid="import-backup-label"
          >
            <div
              className={`inline-flex items-center gap-2 px-5 py-2.5 border-2 rounded-xl text-sm font-bold transition-all duration-200 select-none ${
                importing
                  ? "border-gray-200 text-gray-400"
                  : "border-gray-300 text-gray-700 hover:border-lime-400 hover:text-lime-700 hover:bg-lime-50"
              }`}
            >
              <Upload className="w-4 h-4" />
              {importing ? "Importing..." : "Import Backup (Upload JSON)"}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".json,application/json"
              onChange={handleImport}
              disabled={importing}
              className="hidden"
              data-testid="import-backup-input"
            />
          </label>

          {/* Safety note */}
          <div className="flex items-start gap-2 mt-4">
            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-gray-500">
              This is safe to run multiple times — existing records are updated,
              not duplicated.
            </p>
          </div>

          {/* Import result */}
          {importResult && (
            <div className="mt-4 bg-lime-50 border border-lime-200 rounded-xl p-4 flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-lime-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-lime-800">
                  Import Successful!
                </p>
                <div className="flex gap-6 mt-1.5">
                  <p className="text-xs text-lime-700">
                    <span className="font-semibold">Inserted:</span>{" "}
                    {importResult.inserted}
                  </p>
                  <p className="text-xs text-lime-700">
                    <span className="font-semibold">Updated:</span>{" "}
                    {importResult.updated}
                  </p>
                  <p className="text-xs text-lime-700">
                    <span className="font-semibold">Total:</span>{" "}
                    {importResult.total}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
